import { createStart, createCsrfMiddleware, createMiddleware } from "@tanstack/react-start";
import {
  sentryGlobalFunctionMiddleware,
  sentryGlobalRequestMiddleware,
} from "@sentry/tanstackstart-react";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";


const errorMiddleware = createMiddleware().server(async ({ next, request }) => {
  // Platform routes (email previews, webhooks) authenticate themselves and must pass through.
  if (new URL(request.url).pathname.startsWith("/lovable/")) {
    return next();
  }
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

/**
 * Response security headers.
 *
 * Applied at the request layer so API routes get them too, not just React
 * pages. The CSP is enforcing: it shipped report-only first, then was checked
 * against every marketing, sign-in and workspace route in a real browser with
 * the policy applied before being promoted.
 */

const PUBLIC_PATHS = new Set([
  "/",
  "/auth",
  "/business",
  "/how-it-works",
  "/robots.txt",
  "/sitemap.xml",
  "/favicon.ico",
]);

const isPublicPath = (pathname: string) =>
  PUBLIC_PATHS.has(pathname) ||
  pathname.startsWith("/api/public/") ||
  pathname.startsWith("/assets/") ||
  pathname.startsWith("/_build/");

const SUPABASE_ORIGIN = (() => {
  try {
    return new URL(process.env["SUPABASE_URL"] ?? "https://supabase.co").origin;
  } catch {
    return "https://supabase.co";
  }
})();

const CSP = [
  "default-src 'self'",
  "base-uri 'self'",
  "object-src 'none'",
  "frame-ancestors 'none'",
  "form-action 'self'",
  // Inline scripts/styles are still needed for framework hydration payloads.
  "script-src 'self' 'unsafe-inline' https://js.stripe.com https://*.sentry.io",
  "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
  "font-src 'self' data: https://fonts.gstatic.com",
  "img-src 'self' data: blob: https:",
  `connect-src 'self' ${SUPABASE_ORIGIN} https://*.supabase.co https://*.ingest.de.sentry.io https://*.ingest.sentry.io https://api.stripe.com`,
  "frame-src 'self' https://js.stripe.com https://hooks.stripe.com",
  "worker-src 'self' blob:",
].join("; ");

const securityHeadersMiddleware = createMiddleware().server(async ({ next, request }) => {
  const result = await next();
  const response = (result as { response?: Response }).response;
  if (!response?.headers) return result;

  response.headers.set("Content-Security-Policy", CSP);
  response.headers.set("X-Frame-Options", "DENY");
  if (!response.headers.has("X-Content-Type-Options")) {
    response.headers.set("X-Content-Type-Options", "nosniff");
  }
  if (!response.headers.has("Referrer-Policy")) {
    response.headers.set("Referrer-Policy", "strict-origin-when-cross-origin");
  }
  // Everything outside the marketing/sign-in surface is private workspace UI and
  // must never be indexed, whichever authenticated route it is.
  const { pathname } = new URL(request.url);
  if (!isPublicPath(pathname) && !response.headers.has("X-Robots-Tag")) {
    response.headers.set("X-Robots-Tag", "noindex, nofollow");
  }
  return result;
});

// Start installs this automatically when src/start.ts is absent; defining the
// file opts out, so re-add it explicitly to keep server functions protected
// from cross-site requests.
const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === "serverFn",
});

export const startInstance = createStart(() => ({
  functionMiddleware: [sentryGlobalFunctionMiddleware, attachSupabaseAuth],
  requestMiddleware: [
    sentryGlobalRequestMiddleware,
    securityHeadersMiddleware,
    errorMiddleware,
    csrfMiddleware,
  ],
}));

